import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import styled, { css, keyframes } from 'styled-components';

import { OptionSelector } from '../option-selector/option-selection-wrapper';

import { containerStore, fullscreenStore } from '../../store';
import Header from '../header/header';

export default function BottomControlPanel() {
	const isTopHeaderVisible = use$(fullscreenStore.isTopHeaderVisible);
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const footerRef = useRef<HTMLDivElement>(null);
	const [footerPinnedTop, setFooterPinnedTop] = useState<number | null>(null);

	useLayoutEffect(() => {
		const measure = () => {
			if (window.innerWidth < Breakpoints.b960) {
				setFooterPinnedTop(null);
				return;
			}

			const footer = footerRef.current;
			const panel = footer?.parentElement;
			if (!footer || !panel) return;

			const panelStyle = getComputedStyle(panel);
			const collapsedContentHeight =
				panel.getBoundingClientRect().height -
				parseFloat(panelStyle.paddingTop) -
				parseFloat(panelStyle.paddingBottom) -
				containerStore.bottomPanelExpansion.get();

			setFooterPinnedTop(Math.max(0, Math.round((collapsedContentHeight - footer.offsetHeight) / 2)));
		};

		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, []);

	return (
		<BottomControlPanelContainer $isFullscreen={isFullscreenMode}>
			<OptionSelector />
			<PanelFooter ref={footerRef} $pinnedTop={isFullscreenMode ? null : footerPinnedTop}>
				{!isTopHeaderVisible && <Header />}
			</PanelFooter>
		</BottomControlPanelContainer>
	);
}

const slideUpFade = keyframes`
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const FullscreenPanelMixin = css`
	flex-direction: row;
	align-items: center;
	align-self: stretch;
	gap: 40px;

	width: 100%;
	padding: 12px 16px;
	border-radius: 0;

	background: rgba(255, 255, 255, 0.6);
	backdrop-filter: blur(30px);
	-webkit-backdrop-filter: blur(30px);
`;

const BottomControlPanelContainer = styled.div<{
	$isFullscreen?: boolean;
}>`
	animation: ${slideUpFade} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.15s backwards;

	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 24px;

	width: 100%;
	padding: 12px 16px;
	border-radius: 12px;

	@media (min-width: ${Breakpoints.b960}px) {
		flex-direction: row;
		align-items: flex-start;
		gap: 48px;

		align-self: center;
		width: fit-content;
		max-width: 100%;
		padding: 8px 12px 13px;

		background: transparent;
		overflow: hidden;
	}

	${({ $isFullscreen }) => $isFullscreen && FullscreenPanelMixin}
`;

const PanelFooter = styled.div<{ $pinnedTop: number | null }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;

	@media (min-width: ${Breakpoints.b960}px) {
		flex-shrink: 0;
		${({ $pinnedTop }) =>
			$pinnedTop === null ? 'align-self: center;' : `align-self: flex-start; margin-top: ${$pinnedTop}px;`}
	}
`;
